import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type ProductFamily = Tables<'product_families'>
type Product = Tables<'products'>

export function CatalogPage() {
  const navigate = useNavigate()
  const [families, setFamilies] = useState<ProductFamily[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const [famRes, prodRes] = await Promise.all([
        supabase.from('product_families').select('*').eq('is_active', true).order('sort_order'),
        supabase.from('products').select('*').eq('is_active', true).order('sort_order'),
      ])
      setFamilies(famRes.data || [])
      setProducts(prodRes.data || [])
      setLoading(false)
    }
    load()
  }, [])

  const filtered = products.filter(p => {
    if (selectedFamily && p.family_id !== selectedFamily) return false
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.sku.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  if (loading) return <p style={{ color: 'var(--color-text-muted)', padding: 'var(--space-8)' }}>Loading catalog...</p>

  return (
    <div>
      <PageHeader title="Product Catalog" description="Browse and configure products" />

      {families.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-6)', flexWrap: 'wrap' }}>
          <button className={`${css.tab} ${!selectedFamily ? css.tabActive : ''}`} onClick={() => setSelectedFamily(null)} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2) var(--space-4)' }}>
            All Families
          </button>
          {families.map(f => (
            <button key={f.id} className={`${css.tab} ${selectedFamily === f.id ? css.tabActive : ''}`} onClick={() => setSelectedFamily(f.id)} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2) var(--space-4)' }}>
              {f.name}
            </button>
          ))}
        </div>
      )}

      <input
        type="text"
        placeholder="Search products by name or SKU..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className={css.formInput}
        style={{ maxWidth: 400, marginBottom: 'var(--space-6)' }}
      />

      {filtered.length === 0 ? (
        <EmptyState title="No products found" description="No products match your current filters. Catalog items are managed by Keder administrators." />
      ) : (
        <div className={css.grid3}>
          {filtered.map(product => {
            const family = families.find(f => f.id === product.family_id)
            return (
              <div key={product.id} className={css.cardClickable} onClick={() => navigate(`/gaviota/catalog/${product.id}`)}>
                {product.image_url && (
                  <div style={{ height: 160, background: 'var(--color-bg-subtle)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    <img src={product.image_url} alt={product.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                {!product.image_url && (
                  <div style={{ height: 100, background: 'var(--color-bg-subtle)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
                  </div>
                )}
                {family && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--color-primary)', textTransform: 'uppercase' }}>{family.name}</span>}
                <h3 style={{ fontSize: 'var(--text-base)', marginTop: 'var(--space-1)' }}>{product.name}</h3>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)' }}>
                  {product.sku}{product.product_type ? ` · ${product.product_type.replace(/_/g, ' ')}` : ''}
                </p>
                {product.base_price && (
                  <p style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginTop: 'var(--space-2)' }}>From £{Number(product.base_price).toLocaleString()}</p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
